import { Box, Grid, Stack, Text } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { Button, Tooltip } from "@tfg/ui"
import { useTranslation } from "react-i18next"

import {
  certificateByOrganizationCertificateRequestDtoStatusEnum,
  certificateByOrganizationDtoExtractionOutcomeEnum,
  CertificateByOrganizationDto,
} from "~/gen/api"
import { useUpdateCertificateRequestsMutation } from "~/hooks/useUpdateCertificateRequestsMutation"
import { RejectionModal } from "~/pages/Brand/Certificates/components/RejectionModal"

export type Props = {
  certificateRequestIds: string[]
  certificate: CertificateByOrganizationDto
  onActionSelected: () => void
}

export const CertificateRequestActions = ({
  certificateRequestIds,
  certificate,
  onActionSelected,
}: Props) => {
  const { t } = useTranslation("certificates")

  const {
    mutate: updateCertificateRequest,
    isPending: isUpdatingCertificateRequest,
  } = useUpdateCertificateRequestsMutation({
    certificateId: certificate.id,
    onSettled: onActionSelected,
  })

  const [opened, { open, close }] = useDisclosure()

  const shouldHideAccept =
    certificate.extractionOutcome ===
    certificateByOrganizationDtoExtractionOutcomeEnum.AUTO_ACCEPTED

  return (
    <Stack>
      <Text>{t("certificateRequestActions.question")}</Text>
      <Grid justify="space-between">
        <Grid.Col span={6}>
          <Box style={{ flex: 0.3 }}>
            <Tooltip
              label={t("modal.rejectToolTips")}
              fz="xs"
              maw={250}
              multiline={true}
            >
              <Button
                variant="tertiary-critical"
                w="100%"
                onClick={open}
                disabled={certificateRequestIds.length === 0}
              >
                {t("certificateRequestActions.reject")}
              </Button>
            </Tooltip>
          </Box>
          <RejectionModal
            isSubmitting={isUpdatingCertificateRequest}
            onSubmit={(values) => {
              updateCertificateRequest(
                {
                  data: {
                    ids: certificateRequestIds,
                    update: {
                      status:
                        certificateByOrganizationCertificateRequestDtoStatusEnum.REJECTED,
                      rejectionMessage: values.rejectionMessage,
                    },
                  },
                },
                {
                  onSuccess: () => {
                    close()
                    onActionSelected()
                  },
                }
              )
              close()
            }}
            opened={opened}
            onClose={close}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          {!shouldHideAccept ? (
            <Button
              w="100%"
              variant="secondary"
              onClick={() =>
                updateCertificateRequest(
                  {
                    data: {
                      ids: certificateRequestIds,
                      update: {
                        status:
                          certificateByOrganizationCertificateRequestDtoStatusEnum.ACCEPTED,
                      },
                    },
                  },
                  {
                    onSuccess: () => {
                      close()
                      onActionSelected()
                    },
                  }
                )
              }
              disabled={certificateRequestIds.length === 0}
            >
              {t("certificateRequestActions.accept")}
            </Button>
          ) : null}
        </Grid.Col>
      </Grid>
    </Stack>
  )
}
